import { useState } from 'react';
import { Menu } from './Menu';
import type { SelectOption } from './Select';

/**
 * A single choice from a list, as the custom `Menu` wearing the Select's face — the settings page's
 * one dropdown look (2026-08-01), after the menu-sections row proved the popover reads better than
 * eight native pickers beside it. `Select` (the native element) stays for callers that want the
 * platform's own picker; this is for a column where every dropdown must be the same control.
 *
 * The open state lives here rather than with the caller: a settings page has a dozen of these, and
 * "at most one open" is already what the document-level `pointerdown` close gives — opening one
 * begins with a press outside every other.
 */
export function SelectMenu({
  label,
  value,
  options,
  onChange,
  disabled,
  title,
  className,
}: {
  /** The control's accessible name — the trigger reads as "label: current value". */
  readonly label: string;
  readonly value: string;
  readonly options: readonly SelectOption[];
  readonly onChange: (value: string) => void;
  readonly disabled?: boolean;
  readonly title?: string;
  readonly className?: string;
}) {
  const [open, setOpen] = useState(false);
  const current = options.find((option) => option.id === value);
  return (
    <Menu
      menu={{
        label,
        value: current?.label ?? value,
        sections: [
          {
            title: null,
            items: options.map((option) => ({
              id: option.id,
              label: option.label,
              detail: option.detail ?? null,
              selected: option.id === value,
              ...(option.disabled === true ? { disabled: true } : {}),
            })),
          },
        ],
        note: null,
      }}
      open={open}
      onOpenChange={setOpen}
      onSelect={(id) => {
        onChange(id);
        setOpen(false);
      }}
      variant="select"
      showTriggerLabel={false}
      disabled={disabled}
      title={title}
      className={className}
    />
  );
}
